import { useMsal } from '@azure/msal-react'
import { faImage, faPencil } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Anchor,
  AspectRatio,
  Group,
  Image,
  Loader,
  Stack,
  Text,
} from '@mantine/core'
import { Dropzone } from '@mantine/dropzone'
import { useMediaQuery } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { useState } from 'react'
import { FileWithUrl } from '../../models/FileWithUrl'
import { COLORS } from '../../utils/constants'
import { uploadBlobFromFile } from '../../utils/fileUtils'
import { convertHeicToJpeg } from '../../utils/imageUtils'

interface AdUploadProps {
  onFileUploadComplete: React.Dispatch<React.SetStateAction<FileWithUrl | null>>
  adFile: FileWithUrl | null
  showCanvaText?: boolean
}

export const AdUpload = (props: AdUploadProps) => {
  const { onFileUploadComplete, adFile, showCanvaText = false } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const isMobile = useMediaQuery('(max-width: 768px)')
  const { instance } = useMsal()

  return (
    <Stack>
      <Dropzone
        radius='md'
        onDrop={async (files: File[]) => {
          setIsLoading(true)
          try {
            // Convert HEIC to JPEG if needed
            const processedFile = await convertHeicToJpeg(files[0])

            const fileUrl = await uploadBlobFromFile(
              'advertisements',
              processedFile,
              instance,
            )
            onFileUploadComplete({ fileName: processedFile.name, url: fileUrl })
          } catch (ex) {
            let errorMessage = 'The file failed to upload. Please try again.'

            if (ex instanceof Error) {
              if (ex.message.includes('Failed to convert HEIC')) {
                errorMessage =
                  'Failed to convert HEIC image. Please try a different image format.'
              } else if (ex.message.includes('Failed to upload file')) {
                errorMessage =
                  'Network error. Please check your connection and try again.'
              } else {
                errorMessage = ex.message
              }
            }

            notifications.show({
              title: 'Upload Failed',
              message: errorMessage,
              color: 'red',
              autoClose: 5000,
            })
          } finally {
            setIsLoading(false)
          }
        }}
        onReject={(files: any) => console.log('rejected files', files)}
        maxSize={5 * 1024 ** 2}
        accept={[
          'image/png',
          'image/jpeg',
          'image/jpg',
          'image/gif',
          'image/webp',
          'image/heic',
          'image/heif',
        ]}
        multiple={false}
      >
        <Group
          justify='center'
          gap={isMobile ? 'md' : 'xl'}
          mih={isMobile ? 220 : 320}
          style={{ pointerEvents: 'none' }}
          w='100%'
        >
          {isLoading ? (
            <Loader size={isMobile ? 'md' : 'lg'} />
          ) : adFile != null ? (
            <Stack w='100%' align='center'>
              <AspectRatio ratio={16 / 9} w={isMobile ? '100%' : '80%'}>
                <Image src={adFile.url} radius='md' />
              </AspectRatio>
              <FontAwesomeIcon
                icon={faPencil}
                style={{ alignSelf: 'end', paddingRight: isMobile ? 5 : 10 }}
                size={isMobile ? 'sm' : 'lg'}
              />
            </Stack>
          ) : (
            <AspectRatio ratio={16 / 9} w={isMobile ? '100%' : '80%'}>
              <Group
                gap={isMobile ? 15 : 30}
                h='100%'
                justify='center'
                align='center'
                wrap='nowrap'
                px={isMobile ? 5 : 0}
              >
                <FontAwesomeIcon icon={faImage} size={isMobile ? 'lg' : 'xl'} />
                <Stack gap={isMobile ? 5 : 8}>
                  <Text size={isMobile ? 'md' : 'xl'} fw={600}>
                    {isMobile
                      ? 'Tap to add image'
                      : 'Click here or drag file to add image'}
                  </Text>
                  <Text size={isMobile ? 'xs' : 'sm'} inline lh={1.6}>
                    All advertisements should be full HD resolution (1920x1080)
                    with a 16:9 aspect ratio. Supported formats: PNG, JPEG, GIF,
                    WEBP, HEIC
                  </Text>
                </Stack>
              </Group>
            </AspectRatio>
          )}
        </Group>
      </Dropzone>
      {showCanvaText && (
        <Text
          ta='center'
          c={COLORS.BasicTextGray}
          size={isMobile ? 'xs' : 'sm'}
          px={isMobile ? 10 : 0}
        >
          Don't know where to start? Use a "Presentation" template on{' '}
          <Anchor href='https://www.canva.com/' target='blank'>
            Canva
          </Anchor>{' '}
          to design your ad
        </Text>
      )}
    </Stack>
  )
}
